import { css } from '@emotion/react';
import { faSpinner } from '@fortawesome/pro-regular-svg-icons/faSpinner';
import { Icon } from '@veeva/corgix/icon';
import PropTypes from 'prop-types';
import { useRef, useEffect } from 'react';
import useMessageService from '../../services/i18n/useMessageService';
import NavigationTabs from '../../constants/NavigationTabs';
import { AuthenticatedPaths } from '../../constants/Paths';
import MyAccountLayoutDesktopTab from './MyAccountLayoutDesktopTab';

const propTypes = {
    children: PropTypes.node,
    childrenLoading: PropTypes.bool,
    contentContainerCSS: PropTypes.shape({}),
    headerDescription: PropTypes.node,
    selectedTab: PropTypes.oneOf([
        NavigationTabs.HOME,
        NavigationTabs.SYSTEMS,
        NavigationTabs.STUDIES,
        NavigationTabs.GENERAL,
        NavigationTabs.EMAILS,
        NavigationTabs.CHANGE_PASSWORD,
        NavigationTabs.RECENT_ACTIVITY,
    ]),
};

const MyAccountLayoutDesktop = ({
    children,
    childrenLoading,
    contentContainerCSS,
    headerDescription,
    selectedTab,
}) => {
    const [[myAccountLabel, generalLabel, emailsLabel, changePasswordLabel, recentActivityLabel]] =
        useMessageService([
            'my_account.my_account',
            'my_account.general',
            'my_account.emails',
            'my_account.change_password',
            'my_account.recent_activity',
        ]);

    const tabLabels = {
        [NavigationTabs.GENERAL]: generalLabel,
        [NavigationTabs.EMAILS]: emailsLabel,
        [NavigationTabs.CHANGE_PASSWORD]: changePasswordLabel,
        [NavigationTabs.RECENT_ACTIVITY]: recentActivityLabel,
        [NavigationTabs.HOME]: myAccountLabel,
    };

    const pageTitleCSS = ({ black }) => css`
        font-size: 2rem;
        color: ${black};
        padding-bottom: 3.333rem;
        text-align: center;
        font-weight: bold;
    `;

    const activeTabRef = useRef(null);

    useEffect(() => {
        /**
         * Manually resetting focus to selected tab since each page has its
         * own layout instance, which causes this component to re-mount.
         */
        activeTabRef.current?.focus();
    }, []);

    const contentCSS = ({ white }) => css`
        background-color: ${white};
        padding: 0;
        box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.4);
        border-radius: 8px;
        margin: auto;
        display: flex;
        flex-direction: row;
        flex-grow: 1;
        min-height: 17.333rem; // height of the side tabs container
        ${contentContainerCSS}
    `;

    return (
        <div
            css={css`
                display: flex;
                flex-direction: column;
                min-width: 58.333rem;
                padding: 3.333rem;
                font-size: 1.167rem;
            `}
        >
            <div css={pageTitleCSS}>{myAccountLabel}</div>
            <div css={contentCSS}>
                <div
                    css={css`
                        width: 16.667rem;
                        border-right: 1px solid #d9d9d9;
                        box-sizing: border-box;
                    `}
                >
                    <ul
                        css={css`
                            padding: 3.333rem 0;
                            list-style: none;
                            margin: 0;
                        `}
                    >
                        <MyAccountLayoutDesktopTab
                            active={selectedTab === NavigationTabs.GENERAL}
                            activeTabRef={
                                selectedTab === NavigationTabs.GENERAL ? activeTabRef : null
                            }
                            path={AuthenticatedPaths.MY_ACCOUNT}
                            label={generalLabel}
                        />
                        <MyAccountLayoutDesktopTab
                            active={selectedTab === NavigationTabs.EMAILS}
                            activeTabRef={
                                selectedTab === NavigationTabs.EMAILS ? activeTabRef : null
                            }
                            path={AuthenticatedPaths.EMAILS}
                            label={emailsLabel}
                        />
                        <MyAccountLayoutDesktopTab
                            active={selectedTab === NavigationTabs.CHANGE_PASSWORD}
                            activeTabRef={
                                selectedTab === NavigationTabs.CHANGE_PASSWORD ? activeTabRef : null
                            }
                            path={AuthenticatedPaths.CHANGE_PASSWORD}
                            label={changePasswordLabel}
                        />
                        <MyAccountLayoutDesktopTab
                            active={selectedTab === NavigationTabs.RECENT_ACTIVITY}
                            activeTabRef={
                                selectedTab === NavigationTabs.RECENT_ACTIVITY ? activeTabRef : null
                            }
                            path={AuthenticatedPaths.RECENT_ACTIVITY}
                            label={recentActivityLabel}
                        />
                    </ul>
                </div>
                {childrenLoading ? (
                    <div
                        css={css`
                            align-items: center;
                            display: flex;
                            justify-content: center;
                            width: 41.6667rem;
                        `}
                    >
                        <Icon spin type={faSpinner} color="#555555" size="xl" />
                    </div>
                ) : (
                    <div
                        css={css`
                            box-sizing: border-box;
                            display: flex;
                            flex-direction: column;
                            min-width: 41.6667rem;
                            padding: 3.333rem;
                        `}
                    >
                        <div
                            css={css`
                                display: flex;
                                flex-direction: column;
                                gap: 0.3333rem;
                                margin-bottom: 1.6667rem;
                            `}
                        >
                            <header
                                css={css`
                                    font-size: 1.3333rem;
                                    font-weight: bold;
                                `}
                            >
                                {tabLabels[selectedTab]}
                            </header>
                            {headerDescription}
                        </div>
                        {children}
                    </div>
                )}
            </div>
        </div>
    );
};

MyAccountLayoutDesktop.propTypes = propTypes;

export default MyAccountLayoutDesktop;
